import React, { useState, useEffect } from 'react';
import { removeDuplicates, removeDuplicatesPreview, getInfo } from '../../utils/api';
import { toast } from 'sonner';
import { useT } from '../../i18n';
import { DuplicateGroups } from './DuplicatePlan';

const RemoveDuplicatesOperation = ({ onSuccess, onCancel }) => {
  const t = useT();
  const [mode, setMode] = useState('all');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [keep, setKeep] = useState('first');
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  // The duplicates this setting would remove. Null until asked for; cleared
  // the moment any setting changes, so the list on screen always describes
  // the settings on screen.
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    const loadColumns = async () => {
      try {
        const result = await getInfo();
        setColumns(result.info.column_names || []);
      } catch (error) {
        toast.error(t('form.loadColumnsFailed'));
      }
    };
    loadColumns();
  }, []);

  const toggleColumn = (col) => {
    setPlan(null);
    if (selectedColumns.includes(col)) {
      setSelectedColumns(selectedColumns.filter(c => c !== col));
    } else {
      setSelectedColumns([...selectedColumns, col]);
    }
  };

  const subsetForRequest = () => (mode === 'specific' ? selectedColumns : null);

  // A count cannot be checked. "17 duplicates removed" is not something anyone
  // can verify or argue with, so the rows are named before they go.
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      setPlan(await removeDuplicatesPreview(subsetForRequest(), keep));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('duplicates.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      onSuccess(await removeDuplicates(subsetForRequest(), keep));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('duplicates.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className="label mb-2">{t('duplicates.basedOn')}</label>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              value="all"
              checked={mode === 'all'}
              onChange={(e) => { setPlan(null); setMode(e.target.value); }}
              className="h-4 w-4"
            />
            <span className="text-sm">{t('duplicates.allColumns')}</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              value="specific"
              checked={mode === 'specific'}
              onChange={(e) => { setPlan(null); setMode(e.target.value); }}
              className="h-4 w-4"
            />
            <span className="text-sm">{t('duplicates.someColumns')}</span>
          </label>
        </div>
      </div>

      {mode === 'specific' && (
        <div>
          <label className="label mb-2">{t('form.selectColumns')}</label>
          <div className="card p-4 max-h-48 overflow-y-auto space-y-2">
            {columns.map(col => (
              <label key={col} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(col)}
                  onChange={() => toggleColumn(col)}
                  className="h-4 w-4"
                />
                <span className="text-sm">{col}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="label mb-2">{t('duplicates.keepWhich')}</label>
        <select value={keep} onChange={(e) => { setPlan(null); setKeep(e.target.value); }} className="select">
          <option value="first">{t('duplicates.keepFirst')}</option>
          <option value="last">{t('duplicates.keepLast')}</option>
        </select>
      </div>

      {plan && (
        <div className="card p-4 space-y-3" data-testid="duplicates-plan">
          <h3 className="text-sm font-semibold">
            {plan.rows_removed === 0
              ? t('duplicates.planNothing')
              : t('duplicates.planSummary', {
                  rows: plan.rows_removed,
                  total: plan.rows_total,
                  groups: plan.groups_total,
                })}
          </h3>
          {plan.rows_without_key > 0 && (
            <p className="text-xs text-muted-foreground">
              {t('duplicates.planWithoutKey', { rows: plan.rows_without_key })}
            </p>
          )}
          <DuplicateGroups plan={plan} />
        </div>
      )}

      <div className="flex gap-2 pt-4 border-t">
        {!plan && (
          <button type="submit" disabled={loading} className="btn-primary flex-1" data-testid="preview-remove-duplicates">
            {loading ? t('form.applying') : t('duplicates.preview')}
          </button>
        )}
        {plan && (
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading || plan.rows_removed === 0}
            className="btn-primary flex-1"
            data-testid="apply-remove-duplicates"
          >
            {loading ? t('form.applying') : t('duplicates.confirm', { rows: plan.rows_removed })}
          </button>
        )}
        <button type="button" onClick={onCancel} className="btn-secondary">
          {t('form.cancel')}
        </button>
      </div>
    </form>
  );
};

export default RemoveDuplicatesOperation;